import React, { useEffect, useRef } from "react";

// Public apex landing page. The marketing page is authored as a standalone,
// self-contained static document at `public/about/index.html` (its own styles,
// fonts and scroll-reveal script). To serve it at the site root with the URL
// staying `/` — in both local dev and prod, without duplicating the markup or
// relying on a host-level rewrite — we fetch that document and graft its head
// styles + body into the SPA. The static file remains the single source of
// truth (and is still reachable directly at /about/).
const LANDING_SOURCE = "/about/";
const LANDING_STYLE_MARKER = "data-landing-style";

const LandingPage: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;

        fetch(LANDING_SOURCE)
            .then((response) => {
                if (!response.ok) throw new Error(`landing fetch failed: ${response.status}`);
                return response.text();
            })
            .then((markup) => {
                if (cancelled || !containerRef.current) return;
                const doc = new DOMParser().parseFromString(markup, "text/html");

                // Graft the landing's fonts + styles into <head> once. On the apex
                // the landing owns the whole page, so its global CSS is fine here.
                if (!document.head.querySelector(`[${LANDING_STYLE_MARKER}]`)) {
                    doc.head
                        .querySelectorAll(
                            'style, link[rel="stylesheet"], link[rel="preconnect"], link[rel="preload"]',
                        )
                        .forEach((node) => {
                            const clone = node.cloneNode(true) as HTMLElement;
                            clone.setAttribute(LANDING_STYLE_MARKER, "");
                            document.head.appendChild(clone);
                        });
                }

                // Inject the body markup. Its links (e.g. "Log in" -> /clubs) are
                // plain anchors, so they navigate the top window as normal.
                containerRef.current.innerHTML = doc.body.innerHTML;

                // innerHTML does not execute <script> tags, so recreate the
                // landing's inline scripts (the scroll-reveal IntersectionObserver).
                doc.body.querySelectorAll("script").forEach((source) => {
                    const script = document.createElement("script");
                    if (source.src) script.src = source.src;
                    else script.textContent = source.textContent;
                    containerRef.current?.appendChild(script);
                });
            })
            .catch(() => {
                // If grafting fails for any reason, fall back to the standalone page.
                if (!cancelled) window.location.assign(LANDING_SOURCE);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return <div ref={containerRef} data-testid="landing-page" />;
};

export default LandingPage;
